import React, { useEffect, useRef } from 'react';
import ChatBubble from './ChatBubble';
import TypingIndicator from './TypingIndicator';
import ToolActivityIndicator from './ToolActivityIndicator';
import './ChatMessageList.css';

const SCROLL_LOCK_THRESHOLD_PX = 60;

const ChatMessageList = ({ messages, isStreaming, activeTool, error }) => {
    const endRef = useRef(null);
    const containerRef = useRef(null);
    // Stop auto-scrolling once the user scrolls up to read something.
    const shouldAutoScroll = useRef(true);

    const handleScroll = () => {
        const element = containerRef.current;
        if (!element) return;
        const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
        shouldAutoScroll.current = distanceFromBottom < SCROLL_LOCK_THRESHOLD_PX;
    };

    useEffect(() => {
        if (shouldAutoScroll.current) {
            endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [messages, activeTool, error]);

    const last = messages[messages.length - 1];
    // Models often emit leading newlines before any real text. Treating that as
    // "content" hid the typing indicator and left an empty bubble on screen, which
    // read as a frozen stream. Whitespace-only counts as nothing rendered yet.
    const hasVisibleText = Boolean(last?.content?.trim());
    const awaitingFirstToken = isStreaming && last?.role === 'assistant' && !hasVisibleText && !activeTool;

    return (
        <div className='chat-messages' ref={containerRef} onScroll={handleScroll}>
            {messages
                .filter((message) => message.status !== 'streaming' || Boolean(message.content.trim()))
                .map((message) => (
                <ChatBubble
                    key={message.id}
                    role={message.role}
                    content={message.content}
                    isStreaming={message.status === 'streaming' && Boolean(message.content.trim())}
                />
            ))}

            {activeTool && <ToolActivityIndicator label={activeTool.label} />}
            {awaitingFirstToken && <TypingIndicator />}
            {error && <div className='chat-messages__error'>{error}</div>}

            <div ref={endRef} />
        </div>
    );
};

export default ChatMessageList;
